import type { BoundingRegion } from "@faro-lotv/service-wires";
import {
  BufferGeometry,
  Float32BufferAttribute,
  ShapeUtils,
  Vector2,
  type Vector3Tuple,
} from "three";
import { imagePointToSphere } from "./bounding-box-geometry";

/** Fewer points than this cannot describe an area. */
const MIN_POLYGON_POINTS = 3;

/**
 * @param region The region to check.
 * @returns true if the region encloses an area that can be drawn.
 */
export function isDrawableBoundingRegion({ polygon }: BoundingRegion): boolean {
  return polygon.length >= MIN_POLYGON_POINTS;
}

/**
 * Build the filled surface of the regions, on the unit sphere.
 *
 * The panorama camera sits at the center of the sphere, so a flat triangle with its
 * vertices on the sphere covers exactly the same pixels as the curved one. That makes
 * a plain 2D triangulation of the outline enough, no subdivision needed.
 *
 * @param regions The regions to fill.
 * @returns The triangulated geometry, in pano-local space.
 */
export function boundingRegionsToSphereGeometry(
  regions: BoundingRegion[],
): BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];

  for (const region of regions) {
    const outline = unwrapSeam(region.polygon);
    if (!isDrawableBoundingRegion(region)) continue;

    const firstVertex = positions.length / 3;
    for (const { x, y } of outline) {
      positions.push(...imagePointToSphere(x, y));
    }
    for (const [a, b, c] of ShapeUtils.triangulateShape(outline, [])) {
      indices.push(firstVertex + a, firstVertex + b, firstVertex + c);
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  return geometry;
}

/**
 * Build the perimeter of the regions as a list of line segments.
 *
 * @param regions The regions to outline.
 * @returns The start and end point of every segment, flattened as x,y,z triplets.
 */
export function boundingRegionsToOutlineSegments(
  regions: BoundingRegion[],
): number[] {
  const positions: number[] = [];

  for (const region of regions) {
    const outline = unwrapSeam(region.polygon);
    if (!isDrawableBoundingRegion(region)) continue;

    for (const [index, point] of outline.entries()) {
      const next = outline[(index + 1) % outline.length];
      positions.push(
        ...imagePointToSphere(point.x, point.y),
        ...imagePointToSphere(next.x, next.y),
      );
    }
  }

  return positions;
}

/**
 * Compute the anchor of the label of the regions, the top-left corner of the area they span.
 *
 * @param regions The regions to anchor the label to.
 * @returns The anchor position on the unit sphere, in pano-local space.
 */
export function boundingRegionsTopLeftLocal(
  regions: BoundingRegion[],
): Vector3Tuple {
  const points = regions.flatMap((region) => unwrapSeam(region.polygon));
  const x = Math.min(...points.map((point) => point.x));
  const y = Math.min(...points.map((point) => point.y));
  return imagePointToSphere(x, y);
}

/**
 * Move the points that lie on the other side of the wraparound edge of the panorama image
 * next to their neighbors, so a region crossing that edge stays a single connected shape.
 *
 * @param polygon The outline of a region.
 * @returns The outline points, with x no longer limited to the [0,1] range.
 */
function unwrapSeam(polygon: BoundingRegion["polygon"]): Vector2[] {
  const points: Vector2[] = [];
  for (const { x, y } of polygon) {
    const previousX = points.at(-1)?.x ?? x;
    // Shift by the whole number of image widths that brings the point closest to the previous one.
    points.push(new Vector2(x + Math.round(previousX - x), y));
  }
  return points;
}
